<style>
@import '@/assets/css/custom.css';
</style>

<template>
	<div>
		<Breadcrumbs main="Accounts" title="User payment" />

		<div class="container-fluid">
			<div class="row mb-3">
				<div class="col-12">
					<router-link :to="{ name: 'user_accounts' }" class="btn btn-light btn-sm">
						<i class="icofont icofont-arrow-left me-1"></i> Back to user accounts
					</router-link>
				</div>
			</div>

			<div class="row">
				<div class="col-lg-4 mb-3">
					<div class="card h-100 border-primary">
						<div class="card-body">
							<h6 class="text-muted text-uppercase small mb-2">User</h6>
							<p class="mb-1 fw-bold">{{ displayUserName }}</p>
							<p class="mb-2 text-muted small">{{ displayUserMobile }}</p>
							<div>
								<span class="badge rounded-pill bg-info text-dark" v-if="paymentUserType === '1'">Dealer</span>
								<span class="badge rounded-pill bg-secondary" v-else-if="paymentUserType === '2'">Customer</span>
								<span class="badge rounded-pill bg-light text-dark" v-else>—</span>
							</div>
						</div>
					</div>
				</div>
				<div class="col-lg-8 mb-3">
					<div class="card h-100 bg-light">
						<div class="card-body">
							<div class="row text-center">
								<div class="col-md-3">
									<small class="text-muted d-block">Pending renewals (total)</small>
									<span class="fw-bold text-danger">₹{{ formatMoney(totalRenewalPendingAmount) }}</span>
								</div>
								<div class="col-md-3">
									<small class="text-muted d-block">Pending services (total)</small>
									<span class="fw-bold text-danger">₹{{ formatMoney(totalServicePendingAmount) }}</span>
								</div>
								<div class="col-md-3">
									<small class="text-muted d-block">Pending sales (total)</small>
									<span class="fw-bold text-danger">₹{{ formatMoney(totalSalesPendingAmount) }}</span>
								</div>
								<div class="col-md-3">
									<small class="text-muted d-block">This payment amount</small>
									<span class="fw-bold text-primary fs-5">₹{{ formatMoney(form.amount) }}</span>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div class="card">
				<div class="card-body">
					<div v-if="loadingInitial" class="text-center py-5">
						<div class="spinner-border text-primary" role="status"></div>
						<p class="mt-2 text-muted small">Loading pending items…</p>
					</div>

					<form v-else @submit.prevent="submitPayment">
						<div class="row">
							<div class="col-md-4 form-group mt-2">
								<label class="control-label">Payment for<span class="text-danger">*</span></label>
								<select class="form-select" v-model="form.payment_for" @change="onPaymentForChange">
									<option value="">— Select —</option>
									<option value="1">Renewal repayment</option>
									<option value="2">Work service repayment</option>
									<option value="3">Deposit only (advance)</option>
									<option value="4">Sales repayment</option>
								</select>
								<div v-if="errors.payment_for" class="text-danger small mt-1">{{ errors.payment_for }}</div>
							</div>
							<div class="col-md-6 form-group mt-2" v-if="!paymentUserType">
								<label class="control-label">Payment user type<span class="text-danger">*</span></label>
								<select class="form-select" v-model="form.payment_user_type">
									<option value="">— Select —</option>
									<option value="1">Dealer</option>
									<option value="2">Customer</option>
								</select>
								<small class="text-muted">Set automatically when opened from user accounts.</small>
								<div v-if="errors.payment_user_type" class="text-danger small mt-1">{{ errors.payment_user_type }}</div>
							</div>
						</div>

						<!-- Renewal allocations -->
						<div v-if="form.payment_for === '1'" class="mt-3 position-relative">
							<div
								v-if="refreshingLists"
								class="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-start justify-content-center pt-5 bg-white bg-opacity-75"
								style="z-index: 2"
							>
								<div class="spinner-border spinner-border-sm text-primary" role="status"></div>
							</div>
							<h6 class="border-bottom pb-2">Apply to pending renewals</h6>
							<p class="small text-muted">Enter amount to apply for each renewal. Sum should match total payment below.</p>
							<div class="row mb-2">
								<div class="col-md-8 col-lg-6">
									<input
										type="text"
										class="form-control form-control-sm"
										placeholder="Search renewals (IMEI, vehicle…) — updates as you type"
										v-model="renewalKeyword"
										@input="onRenewalKeywordInput"
									/>
								</div>
							</div>
							<div class="table-responsive">
								<table class="table table-sm table-bordered align-middle">
									<thead class="table-light">
										<tr align="center">
											<th>IMEI</th>
											<th>Vehicle</th>
											<th>Balance due</th>
											<th style="min-width: 120px">Apply (₹)</th>
										</tr>
									</thead>
									<tbody v-if="renewalRows.length">
										<tr v-for="row in renewalRows" :key="'r-' + row.id" align="center">
											<td class="text-start small">{{ row.imei || "—" }}</td>
											<td>{{ row.vehicle_no || "—" }}</td>
											<td>₹{{ formatMoney(row.balance_amount) }}</td>
											<td>
												<input
													type="number"
													class="form-control form-control-sm"
													min="0"
													step="0.01"
													:placeholder="'max ' + formatMoney(row.balance_amount)"
													v-model="renewalAmounts[row.id]"
													@input="syncAmountFromAllocations"
												/>
											</td>
										</tr>
									</tbody>
									<tbody v-else>
										<tr>
											<td colspan="4" class="text-center text-muted py-3">No pending renewals</td>
										</tr>
									</tbody>
								</table>
							</div>
							<nav v-if="renewalPagination.length" class="d-flex justify-content-end">
								<ul class="pagination pagination-sm mb-0">
									<li
										class="page-item"
										v-for="(page, idx) in renewalPagination"
										:key="'rp-' + idx"
										:class="{ disabled: !page.url, active: page.active }"
									>
										<a class="page-link" href="#" @click.prevent="onRenewalPage(page)">{{ formatPaginationLabel(page.label) }}</a>
									</li>
								</ul>
							</nav>
							<p class="small mb-0 mt-2">
								Allocated: <strong>₹{{ formatMoney(sumRenewalAllocated) }}</strong>
							</p>
						</div>

						<!-- Service allocations -->
						<div v-if="form.payment_for === '2'" class="mt-3 position-relative">
							<div
								v-if="refreshingLists"
								class="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-start justify-content-center pt-5 bg-white bg-opacity-75"
								style="z-index: 2"
							>
								<div class="spinner-border spinner-border-sm text-primary" role="status"></div>
							</div>
							<h6 class="border-bottom pb-2">Apply to pending work services</h6>
							<p class="small text-muted">Enter amount to apply for each service. Sum should match total payment below.</p>
							<div class="row mb-2">
								<div class="col-md-8 col-lg-6">
									<input
										type="text"
										class="form-control form-control-sm"
										placeholder="Search services… — updates as you type"
										v-model="serviceKeyword"
										@input="onServiceKeywordInput"
									/>
								</div>
							</div>
							<div class="table-responsive">
								<table class="table table-sm table-bordered align-middle">
									<thead class="table-light">
										<tr align="center">
											<th>Service</th>
											<th>Details</th>
											<th>Balance due</th>
											<th style="min-width: 120px">Apply (₹)</th>
										</tr>
									</thead>
									<tbody v-if="serviceRows.length">
										<tr v-for="row in serviceRows" :key="'s-' + row.id" align="center">
											<td class="small">{{ serviceTypeLabel(row.service_type) }}</td>
											<td class="text-start small">{{ row.details || "—" }}</td>
											<td>₹{{ formatMoney(row.balance_amount) }}</td>
											<td>
												<input
													type="number"
													class="form-control form-control-sm"
													min="0"
													step="0.01"
													v-model="serviceAmounts[row.id]"
													@input="syncAmountFromAllocations"
												/>
											</td>
										</tr>
									</tbody>
									<tbody v-else>
										<tr>
											<td colspan="4" class="text-center text-muted py-3">No pending work services</td>
										</tr>
									</tbody>
								</table>
							</div>
							<nav v-if="servicePagination.length" class="d-flex justify-content-end">
								<ul class="pagination pagination-sm mb-0">
									<li
										class="page-item"
										v-for="(page, idx) in servicePagination"
										:key="'sp-' + idx"
										:class="{ disabled: !page.url, active: page.active }"
									>
										<a class="page-link" href="#" @click.prevent="onServicePage(page)">{{ formatPaginationLabel(page.label) }}</a>
									</li>
								</ul>
							</nav>
							<p class="small mb-0 mt-2">
								Allocated: <strong>₹{{ formatMoney(sumServiceAllocated) }}</strong>
							</p>
						</div>

						<!-- Sales allocations -->
						<div v-if="form.payment_for === '4'" class="mt-3 position-relative">
							<div
								v-if="refreshingLists"
								class="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-start justify-content-center pt-5 bg-white bg-opacity-75"
								style="z-index: 2"
							>
								<div class="spinner-border spinner-border-sm text-primary" role="status"></div>
							</div>
							<h6 class="border-bottom pb-2">Apply to pending sales</h6>
							<p class="small text-muted">Enter amount to apply for each sale. Sum should match total payment below.</p>
							<div class="row mb-2">
								<div class="col-md-8 col-lg-6">
									<input
										type="text"
										class="form-control form-control-sm"
										placeholder="Search sales (invoice, bank ref…) — updates as you type"
										v-model="salesKeyword"
										@input="onSalesKeywordInput"
									/>
								</div>
							</div>
							<div class="table-responsive">
								<table class="table table-sm table-bordered align-middle">
									<thead class="table-light">
										<tr align="center">
											<th>Invoice</th>
											<th>Total</th>
											<th>Paid amount</th>
											<th>Balance due</th>
											<th style="min-width: 120px">Apply (₹)</th>
										</tr>
									</thead>
									<tbody v-if="salesRows.length">
										<tr v-for="row in salesRows" :key="'sl-' + row.id" align="center">
											<td class="text-start small">{{ row.invoice_number || "—" }} <br>
												{{ row.converted_created_at}}
											</td>
											<td>₹{{ formatMoney(row.total_amount) }}</td>
											<td>₹{{ formatMoney(row.paid_amount) }}</td>
											<td>₹{{ formatMoney(row.balance_amount) }}</td>
											<td>
												<input
													type="number"
													class="form-control form-control-sm"
													min="0"
													step="0.01"
													:placeholder="'max ' + formatMoney(row.balance_amount)"
													v-model="salesAmounts[row.id]"
													@input="syncAmountFromAllocations"
												/>
											</td>
										</tr>
									</tbody>
									<tbody v-else>
										<tr>
											<td colspan="5" class="text-center text-muted py-3">No pending sales</td>
										</tr>
									</tbody>
								</table>
							</div>
							<nav v-if="salesPagination.length" class="d-flex justify-content-end">
								<ul class="pagination pagination-sm mb-0">
									<li
										class="page-item"
										v-for="(page, idx) in salesPagination"
										:key="'slp-' + idx"
										:class="{ disabled: !page.url, active: page.active }"
									>
										<a class="page-link" href="#" @click.prevent="onSalesPage(page)">{{ formatPaginationLabel(page.label) }}</a>
									</li>
								</ul>
							</nav>
							<p class="small mb-0 mt-2">
								Allocated: <strong>₹{{ formatMoney(sumSalesAllocated) }}</strong>
							</p>
						</div>

						<hr class="my-4" />

						<div class="row">
							<div class="col-md-4 form-group mt-2">
								<label class="control-label">Total amount (₹)<span class="text-danger">*</span></label>
								<input
									class="form-control"
									type="number"
									min="0"
									step="0.01"
									v-model="form.amount"
									placeholder="0.00"
								/>
								<small v-if="form.payment_for === '1' || form.payment_for === '2' || form.payment_for === '4'" class="text-muted">
									Updates when you change allocation amounts; you can adjust manually if needed.
								</small>
								<div v-if="errors.amount" class="text-danger small mt-1">{{ errors.amount }}</div>
							</div>
							<div class="col-md-4 form-group mt-2">
								<label class="control-label">Payment date<span class="text-danger">*</span></label>
								<input class="form-control" type="date" v-model="form.payment_date" />
								<div v-if="errors.payment_date" class="text-danger small mt-1">{{ errors.payment_date }}</div>
							</div>
							<div class="col-md-4 form-group mt-2">
								<label class="control-label">Payment type<span class="text-danger">*</span></label>
								<select class="form-select" v-model="form.payment_type" @change="onPaymentTypeChange">
									<option value="">— Select —</option>
									<option value="1">Cash</option>
									<option value="2">Online</option>
									<option value="3">Both</option>
									<option v-if="form.payment_for !== '3'" value="4">Advance</option>
								</select>
								<div v-if="errors.payment_type" class="text-danger small mt-1">{{ errors.payment_type }}</div>
							</div>
						</div>

						<div class="row" v-if="showAdvanceFromCredit">
							<div class="col-md-4 form-group mt-2">
								<label class="control-label">
									Amount from advance<span class="text-danger">*</span>
								</label>
								<input
									class="form-control"
									type="number"
									step="0.01"
									min="0"
									placeholder="Enter advance amount"
									v-model="form.amount_from_credit"
									:class="{ 'is-invalid': errors.amount_from_credit }"
								/>
								<div v-if="userCreditLoading" class="mt-2">
									<small class="text-success">Loading…</small>
								</div>
								<div v-else-if="userCredit && userCredit.credit_available_amount != null" class="mt-2">
									<small class="text-success">Advance available: ₹{{ userCredit.credit_available_amount }}</small>
								</div>
								<div v-else class="mt-2">
									<small class="text-muted">—</small>
								</div>
								<div v-if="errors.amount_from_credit" class="invalid-feedback d-block">{{ errors.amount_from_credit }}</div>
								<div v-if="userCreditError" class="text-danger small mt-1">{{ userCreditError }}</div>
							</div>
						</div>

						<div class="row" v-if="form.payment_type === '1' || form.payment_type === '3'">
							<div class="col-md-4 form-group mt-2">
								<label class="control-label">Cash Amount (₹)<span class="text-danger">*</span></label>
								<input class="form-control" type="number" min="0" step="0.01" v-model="form.cash_in_hand" />
								<div v-if="errors.cash_in_hand" class="text-danger small mt-1">{{ errors.cash_in_hand }}</div>
							</div>
						</div>
						<div class="row" v-if="form.payment_type === '2' || form.payment_type === '3'">
							<div class="col-md-4 form-group mt-2">
								<label class="control-label">Cash online (₹)<span class="text-danger">*</span></label>
								<input class="form-control" type="number" min="0" step="0.01" v-model="form.cash_online" />
								<div v-if="errors.cash_online" class="text-danger small mt-1">{{ errors.cash_online }}</div>
							</div>
							<div class="col-md-4 form-group mt-2">
								<label class="control-label">Payment / transaction ID<span class="text-danger">*</span></label>
								<input class="form-control" type="text" v-model="form.payment_id" placeholder="UTR / reference" />
								<div v-if="errors.payment_id" class="text-danger small mt-1">{{ errors.payment_id }}</div>
							</div>
							<div class="col-md-4 form-group mt-2">
								<label class="control-label">Bank account<span class="text-danger">*</span></label>
								<select class="form-select" v-model="form.bank_account_id">
									<option value="">— Select bank —</option>
									<option v-for="b in bankAccounts" :key="b.id" :value="String(b.id)">
										{{ b.bank_name }} — {{ b.account_number }}
									</option>
								</select>
								<div v-if="errors.bank_account_id" class="text-danger small mt-1">{{ errors.bank_account_id }}</div>
							</div>
						</div>

						<div class="row mt-4">
							<div class="col-12">
								<button type="submit" class="btn btn-primary" :disabled="submitting">
									<span v-if="submitting" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
									{{ submitting ? "Saving..." : "Submit payment" }}
								</button>
								<router-link :to="{ name: 'user_accounts' }" class="btn btn-light ms-2">Cancel</router-link>
							</div>
						</div>
					</form>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
export default {
	name: "UserAccountPayment",
	data() {
		return {
			token: "",
			userId: null,
			queryUserName: "",
			queryUserMobile: "",
			paymentUserType: "",
			loadingInitial: true,
			refreshingLists: false,
			renewalSearchTimer: null,
			serviceSearchTimer: null,
			salesSearchTimer: null,
			submitting: false,
			bankAccounts: [],
			totalRenewalPendingAmount: "0",
			totalServicePendingAmount: "0",
			totalSalesPendingAmount: "0",
			renewalRows: [],
			serviceRows: [],
			salesRows: [],
			renewalPagination: [],
			servicePagination: [],
			salesPagination: [],
			renewalKeyword: "",
			serviceKeyword: "",
			salesKeyword: "",
			renewalLimit: 50,
			serviceLimit: 50,
			salesLimit: 50,
			renewalAmounts: {},
			serviceAmounts: {},
			salesAmounts: {},
			errors: {},
			userCredit: null,
			userCreditLoading: false,
			userCreditError: "",
			prevShowAdvanceCredit: false,
			form: {
				amount: "",
				payment_date: "",
				payment_type: "",
				cash_online: "",
				cash_in_hand: "",
				bank_account_id: "",
				payment_id: "",
				payment_for: "",
				payment_user_type: "",
				amount_from_credit: "",
			},
		};
	},
	computed: {
		displayUserName() {
			return this.queryUserName || "User #" + this.userId;
		},
		displayUserMobile() {
			return this.queryUserMobile || "";
		},
		sumRenewalAllocated() {
			return this.sumMap(this.renewalAmounts);
		},
		sumServiceAllocated() {
			return this.sumMap(this.serviceAmounts);
		},
		sumSalesAllocated() {
			return this.sumMap(this.salesAmounts);
		},
		/** Advance payment type is hidden for “Deposit only (advance)”; same condition for the credit field */
		showAdvanceFromCredit() {
			return this.form.payment_type === "4" && this.form.payment_for !== "3";
		},
	},
	watch: {
		showAdvanceFromCredit: "onShowAdvanceFromCreditChange",
	},
	created() {
		this.userId = parseInt(this.$route.params.id, 10);
		if (!this.userId) {
			this.$swal("Error", "Invalid user", "error");
			this.$router.push({ name: "user_accounts" });
			return;
		}
		this.queryUserName = this.$route.query.user_name || "";
		this.queryUserMobile = this.$route.query.user_mobile || "";
		this.paymentUserType = this.$route.query.payment_user_type != null ? String(this.$route.query.payment_user_type) : "";
		if (this.paymentUserType === "1" || this.paymentUserType === "2") {
			this.form.payment_user_type = this.paymentUserType;
		}
		if (!this.form.payment_date) {
			this.form.payment_date = this.todayIsoDate();
		}
		this.init();
	},
	beforeUnmount() {
		if (this.renewalSearchTimer) clearTimeout(this.renewalSearchTimer);
		if (this.serviceSearchTimer) clearTimeout(this.serviceSearchTimer);
		if (this.salesSearchTimer) clearTimeout(this.salesSearchTimer);
	},
	methods: {
		todayIsoDate() {
			return new Date().toISOString().slice(0, 10);
		},

		getToken() {
			if (localStorage.getItem("airotrack_staff")) {
				const airotrack_staff = JSON.parse(localStorage.getItem("airotrack_staff"));
				if (airotrack_staff.token) {
					this.token = airotrack_staff.token;
					return;
				}
			}
			this.$swal("", "Session expired, please login again", "error");
			this.$router.push({ name: "login" });
		},

		async init() {
			this.getToken();
			await Promise.all([this.fetchBankAccounts(), this.fetchPendingPayments()]);
		},

		fetchBankAccounts() {
			return this.$axios
				.get("/staffs/bankAccounts", { headers: { Authorization: "Bearer " + this.token } })
				.then((res) => {
					const data = res.data.data || res.data;
					this.bankAccounts = Array.isArray(data) ? data : (data?.data || []);
				})
				.catch(() => {
					this.bankAccounts = [];
				});
		},

		pendingListUrl() {
			const p = new URLSearchParams();
			p.set("user_id", String(this.userId));
			p.set("renewal_limit", String(this.renewalLimit));
			p.set("service_limit", String(this.serviceLimit));
			p.set("renewal_keyword", this.renewalKeyword || "");
			p.set("service_keyword", this.serviceKeyword || "");
			p.set("sales_limit", String(this.salesLimit));
			p.set("sales_keyword", this.salesKeyword || "");
			return `/staffs/userPendingPayments?${p.toString()}`;
		},

		fetchPendingPayments(renewalUrl, serviceUrl, salesUrl) {
			this.getToken();
			const isFirstLoad = this.loadingInitial;
			if (!isFirstLoad) {
				this.refreshingLists = true;
			}
			let url = this.pendingListUrl();
			if (renewalUrl) {
				url = renewalUrl;
			} else if (serviceUrl) {
				url = serviceUrl;
			} else if (salesUrl) {
				url = salesUrl;
			}
			return this.$axios
				.get(url, { headers: { Authorization: "Bearer " + this.token } })
				.then((res) => {
					if (res.data.status !== true && res.data.status !== "true") {
						this.renewalRows = [];
						this.serviceRows = [];
						this.salesRows = [];
						return;
					}
					const d = res.data.data || {};
					this.totalRenewalPendingAmount = d.totalRenewalPendingAmount ?? "0";
					this.totalServicePendingAmount = d.totalServicePendingAmount ?? "0";
					this.totalSalesPendingAmount = d.totalSalesPendingAmount ?? "0";
					const rPag = d.renewalPendingPayments;
					const sPag = d.servicePendingPayments;
					const slPag = d.salesPendingPayments;
					this.renewalRows = rPag?.data || [];
					this.serviceRows = sPag?.data || [];
					this.salesRows = slPag?.data || [];
					this.renewalPagination = rPag?.links || [];
					this.servicePagination = sPag?.links || [];
					this.salesPagination = slPag?.links || [];
					this.mergeAmountMaps();
				})
				.catch((err) => {
					console.error(err);
					this.$swal("Error", err.response?.data?.message || "Failed to load pending payments", "error");
					this.renewalRows = [];
					this.serviceRows = [];
					this.salesRows = [];
				})
				.finally(() => {
					this.loadingInitial = false;
					this.refreshingLists = false;
				});
		},

		onRenewalKeywordInput() {
			if (this.renewalSearchTimer) clearTimeout(this.renewalSearchTimer);
			this.renewalSearchTimer = setTimeout(() => {
				this.renewalSearchTimer = null;
				this.fetchPendingPayments();
			}, 400);
		},

		onServiceKeywordInput() {
			if (this.serviceSearchTimer) clearTimeout(this.serviceSearchTimer);
			this.serviceSearchTimer = setTimeout(() => {
				this.serviceSearchTimer = null;
				this.fetchPendingPayments();
			}, 400);
		},

		onSalesKeywordInput() {
			if (this.salesSearchTimer) clearTimeout(this.salesSearchTimer);
			this.salesSearchTimer = setTimeout(() => {
				this.salesSearchTimer = null;
				this.fetchPendingPayments();
			}, 400);
		},

		/** Laravel pagination sends HTML entities in labels (e.g. &laquo;); decode for display */
		formatPaginationLabel(label) {
			if (label == null || label === "") return "";
			const el = document.createElement("textarea");
			el.innerHTML = String(label);
			return el.value;
		},

		onRenewalPage(page) {
			if (!page.url) return;
			this.fetchPendingPayments(page.url);
		},

		onServicePage(page) {
			if (!page.url) return;
			this.fetchPendingPayments(null, page.url);
		},

		onSalesPage(page) {
			if (!page.url) return;
			this.fetchPendingPayments(null, null, page.url);
		},

		mergeAmountMaps() {
			for (const row of this.renewalRows) {
				if (this.renewalAmounts[row.id] === undefined) {
					this.renewalAmounts[row.id] = "";
				}
			}
			for (const row of this.serviceRows) {
				if (this.serviceAmounts[row.id] === undefined) {
					this.serviceAmounts[row.id] = "";
				}
			}
			for (const row of this.salesRows) {
				if (this.salesAmounts[row.id] === undefined) {
					this.salesAmounts[row.id] = "";
				}
			}
		},

		onPaymentForChange() {
			this.errors = {};
			if (this.form.payment_for === "1") {
				this.syncAmountFromAllocations();
			} else if (this.form.payment_for === "2") {
				this.syncAmountFromAllocations();
			} else if (this.form.payment_for === "4") {
				this.syncAmountFromAllocations();
			} else if (this.form.payment_for === "3") {
				this.form.amount = "";
				if (this.form.payment_type === "4") {
					this.form.payment_type = "";
					this.clearAdvanceCreditUi();
				}
			}
		},

		onPaymentTypeChange() {
			this.errors = {};
			if (this.form.payment_type === "1") {
				this.form.cash_online = "";
				this.form.payment_id = "";
				this.form.bank_account_id = "";
			} else if (this.form.payment_type === "2") {
				this.form.cash_in_hand = "";
			} else if (this.form.payment_type === "4") {
				this.form.cash_online = "";
				this.form.cash_in_hand = "";
				this.form.payment_id = "";
				this.form.bank_account_id = "";
			} else {
				this.clearAdvanceCreditUi();
			}
		},

		clearAdvanceCreditUi() {
			this.form.amount_from_credit = "";
			this.userCredit = null;
			this.userCreditError = "";
			this.userCreditLoading = false;
			this.prevShowAdvanceCredit = false;
		},

		async onShowAdvanceFromCreditChange(visible) {
			if (!visible) {
				this.clearAdvanceCreditUi();
				return;
			}
			const firstOpen = !this.prevShowAdvanceCredit;
			if (firstOpen && this.form.amount) {
				this.form.amount_from_credit = this.form.amount;
				this.errors.amount_from_credit = "";
			}
			await this.fetchUserCredit();
			this.prevShowAdvanceCredit = true;
		},

		async fetchUserCredit() {
			if (!this.userId) return;
			try {
				this.getToken();
				this.userCreditLoading = true;
				this.userCreditError = "";
				const response = await this.$axios.get("/staffs/getUserCredit", {
					params: { user_id: this.userId },
					headers: { Authorization: "Bearer " + this.token },
				});
				const ok = response.data?.status === true || response.data?.status === "true";
				if (!ok) {
					this.userCredit = null;
					this.userCreditError = response.data?.message || "Failed to fetch credit balance";
					return;
				}
				this.userCredit = response.data?.data?.user_credit || null;
			} catch (e) {
				this.userCredit = null;
				this.userCreditError = e?.response?.data?.message || "Failed to fetch credit balance";
			} finally {
				this.userCreditLoading = false;
			}
		},

		syncAmountFromAllocations() {
			if (this.form.payment_for === "1") {
				const s = this.sumMap(this.renewalAmounts);
				this.form.amount = s > 0 ? s.toFixed(2) : this.form.amount;
			} else if (this.form.payment_for === "2") {
				const s = this.sumMap(this.serviceAmounts);
				this.form.amount = s > 0 ? s.toFixed(2) : this.form.amount;
			} else if (this.form.payment_for === "4") {
				const s = this.sumMap(this.salesAmounts);
				this.form.amount = s > 0 ? s.toFixed(2) : this.form.amount;
			}
		},

		sumMap(obj) {
			let t = 0;
			for (const k of Object.keys(obj)) {
				const n = parseFloat(obj[k]);
				if (!isNaN(n) && n > 0) t += n;
			}
			return Math.round(t * 100) / 100;
		},

		formatMoney(value) {
			const n = parseFloat(value);
			if (isNaN(n)) return "0.00";
			return n.toFixed(2);
		},

		serviceTypeLabel(t) {
			const x = Number(t);
			return x === 1
				? "SIM Change"
				: x === 2
					? "Repair"
					: x === 3
						? "Replacement"
						: x === 4
							? "No Data"
							: x === 5
								? "Tracking Issue"
								: x === 6
									? "Hardware Issue"
									: x === 7
										? "Software Issue"
										: x === 8
											? "Alert Fail"
											: x === 9
												? "Other"
												: "N/A";
		},

		buildPairs(amountMap) {
			const pairs = [];
			for (const k of Object.keys(amountMap)) {
				const id = parseInt(k, 10);
				const v = parseFloat(amountMap[k]);
				if (!isNaN(id) && !isNaN(v) && v > 0) pairs.push([id, v]);
			}
			pairs.sort((a, b) => a[0] - b[0]);
			return {
				ids: pairs.map((p) => p[0]),
				amounts: pairs.map((p) => p[1]),
			};
		},

		validate() {
			this.errors = {};
			const eps = 0.02;
			const amount = parseFloat(this.form.amount);
			if (isNaN(amount) || amount < 0) {
				this.errors.amount = "Enter a valid amount";
				return false;
			}
			if (!this.form.payment_for) {
				this.errors.payment_for = "Select payment for";
				return false;
			}
			if (!this.form.payment_date) {
				this.errors.payment_date = "Select payment date";
				return false;
			}
			const put = this.paymentUserType || this.form.payment_user_type;
			if (!put || (put !== "1" && put !== "2")) {
				this.errors.payment_user_type = "Select payment user type";
				return false;
			}
			if (!this.form.payment_type) {
				this.errors.payment_type = "Select payment type";
				return false;
			}
			const pt = this.form.payment_type;
			if (pt === "4") {
				const creditAmt = parseFloat(this.form.amount_from_credit);
				if (!this.form.amount_from_credit || isNaN(creditAmt) || creditAmt <= 0) {
					this.errors.amount_from_credit = "Enter amount from advance";
					return false;
				}
				if (this.userCredit && this.userCredit.credit_available_amount != null) {
					const available = parseFloat(this.userCredit.credit_available_amount);
					if (!isNaN(available) && creditAmt > available) {
						this.errors.amount_from_credit = "Amount from advance cannot exceed available advance";
						return false;
					}
				}
				if (Math.abs(creditAmt - amount) > eps) {
					this.$swal(
						"Validation",
						`Amount from advance (₹${creditAmt.toFixed(2)}) must match total amount (₹${amount.toFixed(2)}).`,
						"warning"
					);
					return false;
				}
			}
			if (pt === "1" || pt === "3") {
				const c = parseFloat(this.form.cash_in_hand);
				if (isNaN(c) || c < 0) {
					this.errors.cash_in_hand = "Enter cash amount";
					return false;
				}
			}
			if (pt === "2" || pt === "3") {
				const o = parseFloat(this.form.cash_online);
				if (isNaN(o) || o < 0) {
					this.errors.cash_online = "Enter online amount";
					return false;
				}
				if (!String(this.form.payment_id || "").trim()) {
					this.errors.payment_id = "Payment ID is required for online payment";
					return false;
				}
				if (!this.form.bank_account_id) {
					this.errors.bank_account_id = "Select bank account";
					return false;
				}
			}

			if (this.form.payment_for === "1") {
				const { ids, amounts } = this.buildPairs(this.renewalAmounts);
				if (!ids.length) {
					this.$swal("Validation", "Enter amount for at least one renewal", "warning");
					return false;
				}
				const sum = amounts.reduce((a, b) => a + b, 0);
				if (Math.abs(sum - amount) > eps) {
					this.$swal(
						"Validation",
						`Total amount (₹${amount.toFixed(2)}) must match sum of renewal allocations (₹${sum.toFixed(2)}).`,
						"warning"
					);
					return false;
				}
			} else if (this.form.payment_for === "2") {
				const { ids, amounts } = this.buildPairs(this.serviceAmounts);
				if (!ids.length) {
					this.$swal("Validation", "Enter amount for at least one work service", "warning");
					return false;
				}
				const sum = amounts.reduce((a, b) => a + b, 0);
				if (Math.abs(sum - amount) > eps) {
					this.$swal(
						"Validation",
						`Total amount (₹${amount.toFixed(2)}) must match sum of service allocations (₹${sum.toFixed(2)}).`,
						"warning"
					);
					return false;
				}
			} else if (this.form.payment_for === "4") {
				const { ids, amounts } = this.buildPairs(this.salesAmounts);
				if (!ids.length) {
					this.$swal("Validation", "Enter amount for at least one sale", "warning");
					return false;
				}
				const sum = amounts.reduce((a, b) => a + b, 0);
				if (Math.abs(sum - amount) > eps) {
					this.$swal(
						"Validation",
						`Total amount (₹${amount.toFixed(2)}) must match sum of sales allocations (₹${sum.toFixed(2)}).`,
						"warning"
					);
					return false;
				}
			}

			if (pt === "1" || pt === "3") {
				const cash = parseFloat(this.form.cash_in_hand) || 0;
				const online = parseFloat(this.form.cash_online) || 0;
				if (Math.abs(cash + online - amount) > eps) {
					this.$swal(
						"Validation",
						`Cash + online (₹${(cash + online).toFixed(2)}) should equal total amount (₹${amount.toFixed(2)}).`,
						"warning"
					);
					return false;
				}
			}
			if (pt === "2") {
				const online = parseFloat(this.form.cash_online) || 0;
				if (Math.abs(online - amount) > eps) {
					this.$swal("Validation", "Online amount must equal total amount for online-only payment.", "warning");
					return false;
				}
			}

			return true;
		},

		submitPayment() {
			if (this.submitting) return;
			if (!this.validate()) return;
			this.submitting = true;
			this.getToken();

			const put = this.paymentUserType || this.form.payment_user_type;
			const payload = {
				user_id: this.userId,
				amount: parseFloat(this.form.amount),
				payment_date: this.form.payment_date,
				payment_type: parseInt(this.form.payment_type, 10),
				payment_for: parseInt(this.form.payment_for, 10),
				payment_user_type: parseInt(put, 10),
			};

			const pt = this.form.payment_type;
			if (pt === "4") {
				payload.amount_from_credit = parseFloat(this.form.amount_from_credit) || 0;
				payload.cash_online = null;
				payload.payment_id = null;
				payload.bank_account_id = null;
				payload.cash_in_hand = null;
			} else {
				payload.amount_from_credit = null;
				if (pt === "2" || pt === "3") {
					payload.cash_online = parseFloat(this.form.cash_online) || 0;
					payload.payment_id = String(this.form.payment_id || "").trim();
					payload.bank_account_id = this.form.bank_account_id ? parseInt(this.form.bank_account_id, 10) : null;
				} else {
					payload.cash_online = null;
					payload.payment_id = null;
					payload.bank_account_id = null;
				}
				if (pt === "1" || pt === "3") {
					payload.cash_in_hand = parseFloat(this.form.cash_in_hand) || 0;
				} else {
					payload.cash_in_hand = null;
				}
			}

			if (this.form.payment_for === "1") {
				const { ids, amounts } = this.buildPairs(this.renewalAmounts);
				payload.renewal_ids = ids;
				payload.renewal_amounts = amounts;
			} else if (this.form.payment_for === "2") {
				const { ids, amounts } = this.buildPairs(this.serviceAmounts);
				payload.service_ids = ids;
				payload.service_amounts = amounts;
			} else if (this.form.payment_for === "4") {
				const { ids, amounts } = this.buildPairs(this.salesAmounts);
				payload.sales_ids = ids;
				payload.sales_amounts = amounts;
			}

			this.$axios
				.post("/staffs/userPayment", payload, { headers: { Authorization: "Bearer " + this.token } })
				.then((res) => {
					if (res.data.status === true || res.data.status === "true") {
						this.$swal("Success", res.data.message || "Payment recorded", "success");
						this.$router.push({ name: "user_accounts" });
					} else {
						this.$swal("Error", res.data.message || "Failed", "error");
					}
				})
				.catch((err) => {
					const msg = err.response?.data?.message;
					const errs = err.response?.data?.errors;
					if (errs && typeof errs === "object") {
						const first = Object.values(errs)[0];
						this.$swal("Error", Array.isArray(first) ? first[0] : msg || "Validation failed", "error");
					} else {
						this.$swal("Error", msg || "Request failed", "error");
					}
				})
				.finally(() => {
					this.submitting = false;
				});
		},
	},
};
</script>
